import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, SecurityContext, signal } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { CookieService, WindowRef } from '@frontend/vanilla/core';
import { map } from 'rxjs/operators';

import { AppConstants, InputType } from '../config/appConstants';
import { UrlConstants } from '../config/urlConstants';
import { DepositInfo, DepositSubmitRequest } from '../model/depositInfo.model';
import { QDInstrument } from '../model/qdInstrument.model';
import { ErrorCode, ErrorTemplate, ResponseStatus } from '../shared/error.constants';
import { BehaviorDataService } from './behaviorData.service';
import { DataService } from './data.service';
import { DepositRedirectService } from './depositRedirect.service';
import { GtmService } from './gtm.service';
import { LoaderService } from './loader.service';
import { OptionListService } from './optionList.service';
import { QdApiService } from './qd.service';
import { ResizeService } from './resize.service';
import { StakeAmountService } from './stakeAmount.service';
import { UserDataService } from './userDataService';

const iframeCode = `
<form name="threeDSMethodDataForm" id="threeDSMethodDataForm" method="POST">
<input type="hidden" name="threeDSMethodData" id="threeDSMethodData" />
</form>
`;

@Injectable({
    providedIn: 'root',
})
export class DepositService {
    _conversionRates: any;
    _depositInfo: DepositInfo;
    submitResponse: any;
    dataLoading = signal<boolean>(false);
    showDepositInput = signal<boolean>(false);
    showDepositFailure = signal<boolean>(false);
    showDepositSuccess = signal<boolean>(false);
    currencyConversionMap: any = {};

    arcDepositDecline: any;
    arcDepositBlockStatus: any;
    arcDeclinedDepositContactUsUrl: string;
    billingDescriptor: any;
    paypalDirectBadFunding: any = null;
    isPaypalBadFunding = signal<boolean>(false);
    exchangeRate: number;
    depositSubmitRequest: any;
    showDeclinePage = signal<boolean>(false);
    showPixProcessPage = signal<boolean>(false);
    showODpopUp = signal<boolean>(false);
    vatDeductionAmount = signal<number>(0);
    showVATTaxMessage = signal<boolean>(false);
    constructor(
        private resizer: ResizeService,
        private apiService: QdApiService,
        private dataService: DataService,
        private depositRedirectService: DepositRedirectService,
        private stakeAmountService: StakeAmountService,
        private optionListService: OptionListService,
        private behaviorData: BehaviorDataService,
        private sanitizer: DomSanitizer,
        private gtmService: GtmService,
        private userDataService: UserDataService,
        private cookieService: CookieService,
        private loaderService: LoaderService,
        private windowRef: WindowRef,
        private http: HttpClient,
    ) {
        this.handleResponse = this.handleResponse.bind(this);
        this.handleRedirectionResponse = this.handleRedirectionResponse.bind(this);
        this.initializeDepositInfo();
    }

    set conversionRates(_conversionRates: any) {
        this._conversionRates = _conversionRates;
    }

    get conversionRates() {
        return this._conversionRates;
    }

    set depositInfo(_depositInfo: DepositInfo) {
        this.dataService.depositInfo = _depositInfo;
        this._depositInfo = _depositInfo;
    }

    get depositInfo() {
        return this._depositInfo;
    }

    initializeDepositInfo() {
        if (!this.depositInfo) {
            this.depositInfo = new DepositInfo();
            this.depositInfo.amountInAccountCurrency = '';
            this.depositInfo.amountCharged = '';
            this.depositInfo.amountInfo = {};
            this.depositInfo.bonusInfo = {};
            this.depositInfo.browserInfo = this.setBrowserInfo();
            this.depositInfo.paymentAccountInfo = {};
            this.depositInfo.productPreference = '';
            this.depositInfo.details = {};
        }
    }

    setBrowserInfo() {
        const browserInfo: any = {};
        browserInfo.browserLanguage = window.navigator.language.substring(0, 2);
        browserInfo.browserScreenHeight = screen.availHeight;
        browserInfo.browserScreenWidth = screen.availWidth;
        browserInfo.browserTimeZone = new Date().getTimezoneOffset();
        return browserInfo;
    }

    getAmountInTransactionCurrency() {
        if (this.depositInfo.paymentMethod === InputType.FLASH1VOUCHER) {
            this.depositInfo.amountInfo.nativeAmountString = 0;
        } else {
            const txnAmount = parseFloat(this.depositInfo?.amountInAccountCurrency);
            const txnCurrency = this.depositInfo?.amountInfo?.nativeCurrency;
            const accCurrency = this.optionListService?.selectedOption?.paymentMethodInfo?.minDepositLimit?.currencyCode;
            if (isNaN(txnAmount)) {
                this.depositInfo.amountInfo.nativeAmountString = '';
            } else {
                const key = `${txnCurrency.toUpperCase()}_TO_${accCurrency.toUpperCase()}`;
                this.exchangeRate = 1;
                if (txnCurrency.toUpperCase() !== accCurrency.toUpperCase()) {
                    this.exchangeRate = this.conversionRates[key];
                }
                const exponentZeroCurrencies = this.optionListService?.selectedOption?.paymentAccountInfo?.exponentZeroCurrencies;
                if (exponentZeroCurrencies) {
                    const exponentZeroCurrenciesList = exponentZeroCurrencies.split(',');
                    const fixedPosition = exponentZeroCurrenciesList.indexOf(txnCurrency) > -1 ? 0 : 2;
                    this.depositInfo.amountInfo.nativeAmountString = (txnAmount / this.exchangeRate).toFixed(fixedPosition);
                }
            }
            return this.depositInfo?.amountInfo?.nativeAmountString;
        }
    }

    getFeeAmount(amount: any) {
        const fee = parseFloat(this.optionListService?.selectedOption?.paymentMethodInfo?.formattedFee);
        const feeType = this.optionListService?.selectedOption?.paymentMethodInfo?.feeType;

        const fee_amount = feeType === 'abs' ? fee : (amount * fee) / 100;
        return parseFloat(fee_amount.toFixed(2));
    }

    setAmountCharged() {
        const amount = this.depositInfo?.amountInAccountCurrency;
        this.depositInfo.amountCharged = parseFloat(amount) > 0 ? (parseFloat(amount) + this.getFeeAmount(amount)).toFixed(2) : '';
    }

    showRequiredView(amountRequiredToMeetStake: any) {
        if (amountRequiredToMeetStake > 0) {
            this.stakeAmountService.isFullStakeAmountNotMet = true;
        } else {
            this.stakeAmountService.isFullStakeAmountNotMet = false;
            this.showDepositInput.set(false);
            this.showDepositSuccess.set(true);
            this.depositRedirectService.depositProcess = false;
        }
    }

    handleResponse(response: any) {
        this.dataService.data = response;
        if (response?.status && response?.status === ResponseStatus.SUCCESS) {
            response.instrumentNumber = this.optionListService?.selectedOption?.instrumentNumber
                ? this.optionListService?.selectedOption?.instrumentNumber.toLowerCase()
                : '';
            response.type = this.optionListService?.selectedOption?.paymentMethodInfo?.type.toLowerCase();
            response.paymentMethod = this.optionListService?.selectedOption?.paymentMethodInfo?.paymentMethod;
            response.feeCollected =
                this.depositInfo?.amountCharged != '' && this.depositInfo?.amountInAccountCurrency != ''
                    ? parseFloat(this.depositInfo?.amountCharged) - parseFloat(this.depositInfo?.amountInAccountCurrency)
                    : 0;
            response.amountInAccountCurrency = this.depositInfo?.amountInAccountCurrency ? this.depositInfo?.amountInAccountCurrency : '';
            response.accountCurrency = this.optionListService?.selectedOption?.paymentMethodInfo?.minDepositLimit?.currencyCode;
            response.amount.value = response?.amount?.value ? (Math.round(response?.amount?.value * 100) / 100).toFixed(2) : '';
            response.transactionCurrency = this.depositInfo?.amountInfo?.nativeCurrency;
            response.exchangeRate = (1 / this.exchangeRate).toFixed(2);
            response.amountInTxnCurrency = this.depositInfo?.amountInfo?.nativeAmountString ? this.depositInfo?.amountInfo?.nativeAmountString : '';
            response.amount.currencycode = response?.accountCurrency
                ? response?.accountCurrency
                : this.optionListService?.selectedOption?.paymentMethodInfo?.minDepositLimit?.currencyCode;
            response.billingDescriptor = this.billingDescriptor ? this.billingDescriptor : response?.billingDescriptor;
            this.showDepositSuccess.set(true);
            this.loaderService.depositRedirectInProgress.set(false);
            this.depositRedirectService.depositProcess = false;
            this.showDepositInput.set(false);
            this.showPixProcessPage.set(false);
        } else if (response?.status && response?.status === ResponseStatus.FAILURE) {
            this.showDepositFailure.set(true);
            this.showDepositSuccess.set(false);
            this.showPixProcessPage.set(false);
            this.depositRedirectService.depositProcess = false;
            if (
                this.userDataService?.userData?.configParams?.isBrazilBankRegEnabled &&
                ['2020', '2021', '854', '3'].includes(response?.errorResponse?.errorCode) &&
                ['DEPOSIT_DECLINE'].includes(response?.errorResponse?.templateName?.toUpperCase())
            ) {
                this.showDeclinePage.set(true);
                this.showDepositFailure.set(false);
            }
        }
        this.submitResponse = response;
        this.behaviorData.changeSubmitResponse(response);

        if (response?.status && response?.status === ResponseStatus.SUCCESS) {
            this.stakeAmountService.retrieveAmountRequiredToMeetStake(this.showRequiredView.bind(this));
        } else if (response?.status && response?.status === ResponseStatus.POST_REDIRECT) {
            this.loaderService.depositRedirectInProgress.set(true);
            this.depositRedirectService.depositSubmitResponse = response;
            this.billingDescriptor = response?.billingDescriptor;
            const redirect = {
                redirectionUrl: this.handleUrlSanitization(response.redirectUrl) || '',
                submitJwt: response?.postData?.JWT ? true : false,
                openInCustomTab: response?.openInCustomTab,
                redirectType: 'POST',
                new_3ds2_route: response?.successMessageInfo?.new_3ds2_route === 'Y' || response?.details?.new_3ds2_route === 'Y',
            };
            this.depositRedirectService.updateDespositRedirectInfo(redirect);

            setTimeout(() => {
                this.depositRedirectService.redirectToThirdParty(this.handleResponse);
            });
        } else if (response?.status && response?.status === ResponseStatus.REDIRECT) {
            this.loaderService.depositRedirectInProgress.set(true);
            this.depositRedirectService.depositSubmitResponse = response;
            const redirect = {
                redirectionUrl: this.handleUrlSanitization(response.redirectUrl) || '',
                openInCustomTab: response?.openInCustomTab,
                redirectType: 'GET',
                new_3ds2_route: response?.successMessageInfo?.new_3ds2_route === 'Y' || response?.details?.new_3ds2_route === 'Y',
            };
            this.depositRedirectService.updateDespositRedirectInfo(redirect);
            setTimeout(() => {
                this.depositRedirectService.redirectToThirdParty(this.handleResponse);
            });
        } else if (response?.status && response?.status === ResponseStatus.PENDING) {
            this.depositRedirectService.depositSubmitResponse = response;
            if (response?.paymentMethod == AppConstants.PIX) {
                document.addEventListener('visibilitychange', this.depositRedirectService.handleVisibilityChange);
                this.depositRedirectService.depositProcess = true;
            }
            this.depositRedirectService.checkDepositStatus(this.depositRedirectService?.depositSubmitResponse?.transactionId, this.handleResponse);
        } else if (response?.status && response?.status === ResponseStatus.THREEDS2_REDIRECT) {
            response.instrumentNumber = this.optionListService?.selectedOption?.instrumentNumber
                ? this.optionListService?.selectedOption?.instrumentNumber.toLowerCase()
                : '';
            response.type = this.optionListService?.selectedOption?.paymentMethodInfo?.type?.toLowerCase();
            response.paymentMethod = this.optionListService?.selectedOption?.paymentMethodInfo?.paymentMethod;
            response.feeCollected =
                this.depositInfo?.amountCharged !== '' && this.depositInfo?.amountInAccountCurrency !== ''
                    ? parseFloat(this.depositInfo?.amountCharged) - parseFloat(this.depositInfo?.amountInAccountCurrency)
                    : 0;
            response.amountInAccountCurrency = this.depositInfo?.amountInAccountCurrency ? this.depositInfo?.amountInAccountCurrency : '';
            response.amount.value = response?.amount?.value ? (Math.round(response?.amount?.value * 100) / 100).toFixed(2) : '';
            response.transactionCurrency = this.depositInfo?.amountInfo?.nativeCurrency;
            response.exchangeRate = (1 / this.exchangeRate).toFixed(2);
            response.amountInTxnCurrency = this.depositInfo?.amountInfo?.nativeAmountString ? this.depositInfo?.amountInfo?.nativeAmountString : '';
            response.amount.currencyCode = response?.accountCurrency ? response?.accountCurrency : response?.amount?.currencyCode;
            response.billingDescriptor = response?.billingDescriptor ? response?.billingDescriptor : response.billingDescriptor;
            this.submitResponse = response;
            this.loaderService.depositRedirectInProgress.set(true);
            this.loaderService.isLoading.next(true);
            this.depositRedirectService.depositSubmitResponse = response;
            const redirect = {
                redirectionUrl: this.handleUrlSanitization(response.redirectionUrl) || '',
                redirectionData: response?.postData?.threeDSMethodData,
            };
            this.depositRedirectService.updateDespositRedirectInfo(redirect);
            this.submit3DSMethodData(redirect);
            this.depositRedirectService.check3dDepositStatus(() => {
                this.depositRedirectService.getRedirectData(this.handleRedirectionResponse);
            });
        } else if (response?.status && response?.status === ResponseStatus.REDIRECT_BANK) {
            this.loaderService.depositRedirectInProgress.set(true);
            this.depositRedirectService.depositSubmitResponse = response;
            const redirect = {
                redirectionUrl: this.handleUrlSanitization(response.redirectUrl) || '',
                creq: response?.postData?.creq,
                submitCreq: true,
                openInCustomTab: response?.openInCustomTab,
                redirectType: 'POST',
                new_3ds2_route: response?.successMessageInfo?.new_3ds2_route === 'Y' || response?.details?.new_3ds2_route === 'Y',
            };
            this.depositRedirectService.updateDespositRedirectInfo(redirect);
            setTimeout(() => {
                this.depositRedirectService.redirectToThirdParty(this.handleResponse);
            });
        } else if (response?.status == ResponseStatus.BANK_DETAIL_MISMATCH && this.userDataService?.userData?.configParams?.pixBrInfo?.isSeamless) {
            this.optionListService.redirectToMainCashier(`&type=transactionStatus&txnId=${response?.transactionId}`);
        } else {
            this.loaderService.depositRedirectInProgress.set(false);
            this.loaderService.isLoading.next(false);
            this.behaviorData.changeCVV2('');
            this.stakeAmountService.isFullStakeAmountNotMet = true;
            if (response?.errorResponse?.errorCode) {
                const { errorCode, errorMessage } = response.errorResponse;
                if (errorCode && ['2400', '2401', '2402'].includes(response.errorResponse.errorCode) && response.paymentMethod == 'FLASH1VOUCHER') {
                    this.behaviorData.changeFlashVoucherErrorDetails(response?.errorResponse);
                } else if (response.errorResponse.errorCode == '301') {
                    this.behaviorData.changebwinPLkycverification(response?.errorResponse);
                    this.behaviorData.changeHideInputPage(true);
                } else if (response.errorResponse.errorCode == '302') {
                    this.behaviorData.changekycPLlimitverification(response?.errorResponse);
                    this.behaviorData.changeHideInputPage(true);
                } else if (errorCode && (errorCode === '168' || errorCode === '169' || errorCode === '171' || errorCode === '170')) {
                    if (errorCode === '168') {
                        this.behaviorData.changeAdaInteractionMessage(true);
                        this.gtmService.raiseEventForUnusualDeposit(
                            'unusualDepositInteractionMessage',
                            'quick deposit',
                            this.depositInfo?.amountInfo?.nativeAmountString,
                        );
                    } else if (errorCode === '169') {
                        this.behaviorData.changeInPlayBreak(true);
                    } else if (errorCode === '171') {
                        this.behaviorData.changeSessionTimeoutStatus(true);
                    } else if (errorCode === '170') {
                        if (errorMessage == 'arc_deposit_decline_intervention_message') {
                            this.arcDepositDecline = '';
                            this.behaviorData.changeArcDepositBlockStatus(response?.userARCDeclinedDepositStatus);
                            const ccData = { payment: { error: '' } };
                            ccData.payment.error = 'maximum allowed decline deposits reached - deposit ability blocked for 24h';
                            this.gtmService.raiseComponentEvent('paymentError', ccData);
                        } else if (errorMessage == 'arc_deposit_decline_interaction_message') {
                            this.arcDepositDecline = response?.errorResponse;
                            this.gtmService.raiseEventForArcUnusualDeposit('unusualArcDepositInteractionMessage', 'quick deposit');
                        }
                    }
                } else if (
                    errorCode === '2046' &&
                    ['2046 : INSUFFICIENT_FUNDS', '2046 : TRANSACTION_REFUSED', '2046 : INSTRUMENT DECLINED'].includes(errorMessage)
                ) {
                    if (['2046 : TRANSACTION_REFUSED', '2046 : INSTRUMENT DECLINED'].includes(errorMessage)) {
                        this.behaviorData?.changePaypalBadFunding(true);
                    }
                    this.paypalDirectBadFunding = response.errorResponse;
                } else if (
                    errorCode === ErrorCode.maxFailDepEnhancementErrorCode &&
                    response?.errorResponse?.templateName == ErrorTemplate.maxFailDepEnhancementErrorTemplate &&
                    response?.errorResponse?.additionalData?.userMaxFailDepStatus
                ) {
                    this.behaviorData.changeMaxFailDepEnhancement.next(response?.errorResponse?.additionalData?.userMaxFailDepStatus);
                }
            } else {
                this.showDepositFailure.set(true);
                this.showDepositSuccess.set(false);
                this.showPixProcessPage.set(false);
            }
        }
        this.resizer.resizeCashier();
    }

    handleUrlSanitization(url: string) {
        return this.sanitizer.sanitize(SecurityContext.URL, this.sanitizer.bypassSecurityTrustUrl(url));
    }

    handleRedirectionResponse(response: any) {
        if (response?.status && response?.status === ResponseStatus.SUCCESS) {
            this.dataService.data = response;
            this.submitResponse.status = ResponseStatus.SUCCESS;
            this.behaviorData.changeSubmitResponse(response);
            this.stakeAmountService.retrieveAmountRequiredToMeetStake(this.showRequiredView.bind(this));
        } else if (response?.status && response?.status === ResponseStatus.REDIRECT_BANK) {
            const redirect = {
                redirectionUrl: this.handleUrlSanitization(response.redirectUrl) || '',
                creq: response?.redirectData?.redirectPostData,
                submitCreq: true,
                openInCustomTab: response?.openInCustomTab,
                redirectType: 'POST',
                new_3ds2_route: response?.successMessageInfo?.new_3ds2_route === 'Y' || response?.details?.new_3ds2_route === 'Y',
            };
            this.depositRedirectService.updateDespositRedirectInfo(redirect);
            setTimeout(() => {
                this.depositRedirectService.redirectToThirdParty(this.handleResponse);
            });
        } else {
            this.depositRedirectService.checkDepositStatus(this.depositRedirectService?.depositSubmitResponse?.transactionId, this.handleResponse);
        }
    }

    submit3DSMethodData(data: any) {
        const iframe = document.getElementById('threeDS2redirect') as HTMLIFrameElement;

        const iframeDoc = iframe?.contentWindow?.document;
        iframeDoc?.write(iframeCode);
        (<HTMLFormElement>iframeDoc?.getElementById('threeDSMethodDataForm')).action = data?.redirectionUrl;
        (<HTMLFormElement>iframeDoc?.getElementById('threeDSMethodData')).value = data?.redirectionData;
        (<HTMLFormElement>iframeDoc?.getElementById('threeDSMethodDataForm')).submit();
    }

    submitDeposit() {
        this.setGaClientAndSessionInfo();
        document.removeEventListener('visibilitychange', this.depositRedirectService.handleVisibilityChange);
        delete this.depositInfo.paymentAccountInfo.cvv2;
        this.apiService
            .post(UrlConstants.DEPOSIT_SUBMIT, this.depositInfo)
            .pipe(
                map(
                    (response: HttpResponse<any>) => {
                        this.handleResponse(response);
                    },
                    (rejected: HttpResponse<any>) => {
                        this.handleResponse(rejected);
                    },
                ),
            )
            .subscribe(() => this.dataLoading.set(false));
    }
    // ApplePay  Deposit  Call
    async applePayDepositSubmit(depositSubmitReq: DepositSubmitRequest) {
        this.setGaClientAndSessionInfo();
        this.depositSubmitRequest = depositSubmitReq;
        // this.setWorldPaySessionFor3ds(depositSubmitReq);
        const sessionInfo = JSON.parse(sessionStorage.getItem('sessionInfo') as any);
        const tempToken = sessionStorage.getItem('tempToken');
        tempToken && depositSubmitReq.paymentAccountInfo ? (depositSubmitReq.paymentAccountInfo.tempToken = tempToken) : null;
        // this.sessionInfo.registrationSuccessSnippet && this.userInfo.configParams.eidAuthenticationEnabled=='Y' ? depositSubmitReq.paymentAccountInfo.isNewRegistration = 'Y' : 'N';
        const depositSubmitUrl = UrlConstants.DEPOSIT_SUBMIT_URI;
        return await this.apiService
            .post(depositSubmitUrl + `?language=${sessionInfo?.LANG_ID ? sessionInfo.LANG_ID : 'en'}&source=QD`, depositSubmitReq)
            .toPromise()
            .then((data) => {
                this.handleResponse(data);
                if (data?.status === 'SUCCESS') {
                    return data;
                }
            });
    }

    // ApplePay Token  Call

    registerCardDetails(url: string, cardDetails: any, cardToken?: any): any {
        if (!this.isNullOrEmpty(url) && url !== 'token') {
            let headers = new HttpHeaders({
                'Content-Type': 'application/json',
            });
            if (cardToken != null) {
                headers = headers.append('token', cardToken);
            }
            const httpOptions = {
                headers,
                observe: 'response' as 'body',
            };
            return this.http.post(url, cardDetails, httpOptions).toPromise();
        }
    }

    isNullOrEmpty(str: string | null | undefined): boolean {
        return !str || str.trim() === '';
    }
    affMaxLossLimitInterceptorCall(status: any) {
        return this.apiService.post(UrlConstants.AFF_MAXLOSS_TOGGLE_INTERCEPTOR + `?status=${status}`, '').toPromise();
    }
    setGaClientAndSessionInfo() {
        let gaSessionId = '';
        const userData = this.userDataService.userData;
        if (userData?.configParams?.ga_cookie_key) {
            gaSessionId = this.cookieService.get(userData?.configParams?.ga_cookie_key);
            if (gaSessionId) {
                gaSessionId = gaSessionId.split('.')[2] ?? '';
            }
        }
        const window = this.windowRef.nativeWindow;
        const gaClientId = window?.gaGlobal?.vid || window?.ga?.getAll()[0].get('clientId');
        if (this.depositInfo?.details) {
            this.depositInfo.details.GA_CLIENT_ID = gaClientId;
            this.depositInfo.details.GA_SESSION_ID = gaSessionId;
        } else {
            this.depositInfo.details = { GA_CLIENT_ID: gaClientId, GA_SESSION_ID: gaSessionId };
        }
    }

    setCurrencyConversion(selectedOption: QDInstrument) {
        this.currencyConversionMap = {};
        const accountCurrency = selectedOption?.paymentMethodInfo?.minDepositLimit?.currencyCode?.toUpperCase();
        selectedOption.currencyInfo.currencyList.forEach((currency) => {
            const conversionKey = currency.toUpperCase() + '_TO_' + accountCurrency;
            const value = 1 / this.conversionRates[conversionKey];
            this.currencyConversionMap[currency] = currency === accountCurrency ? value : value.toFixed(4);
        });
    }

    callingSuccessApi(depositSuccessData: any): any {
        return this.apiService
            .put(UrlConstants.DEPOSIT_SUCCESS_URI + '/' + depositSuccessData, '')
            .toPromise()
            .then(
                (data: any) => {
                    return data;
                },
                (error: any) => {
                    return error;
                },
            );
    }
    getLabelName(userInfo: any) {
        const brandName = userInfo.labelInfo.brandId?.toLowerCase() || '';
        if (['sporting', 'sbbrazil'].includes(brandName)) {
            return 'Sportingbet';
        } else if (brandName?.includes('betboo')) {
            return 'Betboo';
        }
        return brandName;
    }
    async closeVATTaxMessage() {
        return await this.apiService
            .post(UrlConstants.CLOSE_VAT_TAX_MESSAGE, this.dataService?.sessionInfo?.userId)
            .toPromise()
            .then(
                (data) => {
                    return data;
                },
                (error) => {
                    return error;
                },
            );
    }
    calculateVATPercentage(value: number) {
        if (value) {
            this.vatDeductionAmount.set(
                Number(((value * this.userDataService?.userData?.configParams?.colombia_deposit_tax_percentage) / 100).toFixed(2)),
            );
        }
    }
}
